import type { SudokuConfig, SudokuPuzzle, SudokuSize } from '../../types'

// ─── Box dimensions per grid size ────────────────────────────────────────────
// 4×4: 2-wide × 2-tall boxes in a 2×2 arrangement
// 6×6: 3-wide × 2-tall boxes in a 2×3 arrangement
// 9×9: 3-wide × 3-tall boxes in a 3×3 arrangement

export const BOX_DIMS: Record<SudokuSize, { w: number; h: number }> = {
  4: { w: 2, h: 2 },
  6: { w: 3, h: 2 },
  9: { w: 3, h: 3 },
}

// ─── Pre-filled cell counts per (size, difficulty) ────────────────────────────

const FILL_COUNTS: Record<SudokuSize, Record<string, number>> = {
  4: { easy: 12, medium: 10, hard: 8  },
  6: { easy: 24, medium: 18, hard: 14 },
  9: { easy: 36, medium: 28, hard: 20 },
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function isValid(
  grid: number[],
  row: number,
  col: number,
  num: number,
  size: SudokuSize,
): boolean {
  const { w, h } = BOX_DIMS[size]

  // Row
  for (let c = 0; c < size; c++) {
    if (grid[row * size + c] === num) return false
  }

  // Column
  for (let r = 0; r < size; r++) {
    if (grid[r * size + col] === num) return false
  }

  // Box
  const boxStartRow = Math.floor(row / h) * h
  const boxStartCol = Math.floor(col / w) * w
  for (let r = boxStartRow; r < boxStartRow + h; r++) {
    for (let c = boxStartCol; c < boxStartCol + w; c++) {
      if (grid[r * size + c] === num) return false
    }
  }

  return true
}

// ─── Solver (backtracking) ────────────────────────────────────────────────────

/**
 * Solve `grid` in-place.
 * Returns true on success.
 * When `randomOrder` is true, numbers are tried in a random order (used for generation).
 */
export function solve(grid: number[], size: SudokuSize, randomOrder = false): boolean {
  const nums = Array.from({ length: size }, (_, i) => i + 1)

  function bt(pos: number): boolean {
    if (pos === size * size) return true
    if (grid[pos] !== 0) return bt(pos + 1)

    const row = Math.floor(pos / size)
    const col = pos % size
    const candidates = randomOrder ? shuffle(nums) : nums

    for (const n of candidates) {
      if (isValid(grid, row, col, n, size)) {
        grid[pos] = n
        if (bt(pos + 1)) return true
        grid[pos] = 0
      }
    }

    return false
  }

  return bt(0)
}

/**
 * Count solutions for `grid`, stopping early once `max` is reached.
 * Use max=2 to distinguish "exactly one solution" from "multiple solutions".
 */
function countSolutions(grid: number[], size: SudokuSize, max = 2): number {
  const g = [...grid]
  let count = 0

  function bt(pos: number): void {
    if (count >= max) return
    if (pos === size * size) { count++; return }
    if (g[pos] !== 0) { bt(pos + 1); return }

    const row = Math.floor(pos / size)
    const col = pos % size

    for (let n = 1; n <= size; n++) {
      if (isValid(g, row, col, n, size)) {
        g[pos] = n
        bt(pos + 1)
        g[pos] = 0
      }
    }
  }

  bt(0)
  return count
}

// ─── Generator ────────────────────────────────────────────────────────────────

/**
 * Generate a fully solved grid using backtracking with a randomised number order.
 */
function generateSolved(size: SudokuSize): number[] {
  const grid = new Array<number>(size * size).fill(0)
  solve(grid, size, true)
  return grid
}

/**
 * Create a puzzle by removing cells from a solved grid.
 * Ensures the resulting puzzle has exactly one solution.
 */
export function generatePuzzle(config: SudokuConfig): SudokuPuzzle {
  const { size, difficulty } = config

  const solution = generateSolved(size)
  const puzzle = [...solution]

  const targetFilled = FILL_COUNTS[size][difficulty]
  const targetBlanks = size * size - targetFilled

  let blanked = 0
  const positions = shuffle(Array.from({ length: size * size }, (_, i) => i))

  for (const pos of positions) {
    if (blanked >= targetBlanks) break

    const backup = puzzle[pos]
    puzzle[pos] = 0

    // Keep the removal only if the puzzle remains uniquely solvable
    if (countSolutions(puzzle, size, 2) === 1) {
      blanked++
    } else {
      puzzle[pos] = backup
    }
  }

  return {
    config,
    given:    puzzle,
    solution,
  }
}
